/**
 * SFrame129 – IAD Step 6: Initial death notifications
 * Refactored to Checklist-based UX for empathetic, immediate coordination with Notes.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ProgressHeader from '../settlement/ProgressHeader';
import ChecklistItem from '../settlement/ChecklistItem'; 
import { useSettlementSession } from '../../hooks/useSettlementSession';
import { loadResidencePanelCompletion, withEstatePath } from '../../utils/secureResidenceFlow';
import { PlannerDataSection } from '../settlement/PlannerDataSection';
import { SETTLEMENT_PLANNER_LAYOUT } from '../settlement/settlementTheme';

interface FrameDataWithNotes {
  completed?: boolean;
  notes?: string;
}

const PHASE = 'iad';
const STEP_ID = 'iad-6';
const OVERVIEW_ROUTE = '/sframe-141';
const isPlanner = true;

const containerClassName = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.container : '';
const contentColumnClassName = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.contentColumn : '';
const titleClassName = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.title : 'font-display text-[28px] leading-tight text-blue-900';
const subtitleClassName = isPlanner ? SETTLEMENT_PLANNER_LAYOUT.subtitle : 'text-sm text-grey-600 mb-6 leading-relaxed';

const NOTIFICATION_PANELS = [
  { frameId: 'frame-notify-identify', title: 'Identify Who to Notify', intro: 'Make a simple list of people and organizations to inform.' },
  { frameId: 'frame-notify-family', title: 'Notify Immediate Family and Close Contacts', intro: 'Inform those closest to the deceased first.' },
  { frameId: 'frame-notify-orgs', title: 'Notify Employers and Key Services', intro: 'Inform employers and important service providers.' },
];

const CHECKLIST_GUIDANCE = [
  {
    what: 'Identify who should be notified about the passing.',
    why: 'Ensures no important person or organization is missed.',
    actions: ['List immediate family and close friends', 'Include employer, caregivers, or neighbors if relevant', 'Identify key service providers (banks, insurance, etc.)', 'Add anyone else who should hear directly'],
  },
  {
    what: 'Reach out to immediate family and close relationships.',
    why: 'They should hear directly and may help with next steps.',
    actions: ['Prioritize closest family members first', 'Call or speak in person when possible', 'Share basic information and next steps', 'Ask for help notifying others if needed'],
  },
  {
    what: 'Notify organizations connected to the deceased.',
    why: 'Prevents issues with accounts, benefits, or responsibilities.',
    actions: ['Notify employer or HR if applicable', 'Contact insurance providers if needed', 'Inform caregivers or service providers', 'Make a simple note of who you’ve contacted'],
  },
];

// Helper to pull relevant contacts from the Estate Plan (Frame 146) if they exist
function renderPlanner146Summary(data: Record<string, unknown>) {
  const contacts = (data.contacts || data.important_contacts) as Record<string, unknown>[] | undefined;
  
  if (!contacts || contacts.length === 0) return null;

  return (
    <div className="space-y-3 text-sm text-stone-800">
      <h4 className="font-semibold text-stone-900">Key contacts from plan</h4>
      <div className="grid gap-2">
        {contacts.map((c, i) => (
          <div key={i} className="flex justify-between border-b border-stone-100 pb-1">
            <span className="font-medium">{typeof c.name === 'string' ? c.name : 'Unnamed Contact'}</span>
            <span className="text-stone-500">{typeof c.relationship === 'string' ? c.relationship : typeof c.role === 'string' ? c.role : ''}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function SFrame129() {
  const { estateId: paramEstateId } = useParams<{ estateId: string }>();
  const estateId = paramEstateId || null;
  const navigate = useNavigate();
  
  const { error, loadFrameData, loadPhaseProgress, updateStepProgress, saveFrameData } = useSettlementSession(estateId);

  const [completedPanels, setCompletedPanels] = useState(0);
  const [panelDone, setPanelDone] = useState<Record<string, boolean>>({});
  const [notes, setNotes] = useState('');

  const refresh = useCallback(async () => {
    if (!estateId) return;

    // Load checklist completion
    const { byFrameId } = await loadResidencePanelCompletion(estateId, loadFrameData);
    const notificationFrameIds = NOTIFICATION_PANELS.map(p => p.frameId);
    const count = Object.keys(byFrameId).filter(id => notificationFrameIds.includes(id) && byFrameId[id]).length;
    
    setCompletedPanels(count);
    setPanelDone(byFrameId);

    // Load notes
    const frameData = await loadFrameData(STEP_ID) as FrameDataWithNotes;
    if (frameData?.notes) setNotes(frameData.notes);
  }, [estateId, loadFrameData]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleToggleComplete = async (frameId: string, newState: boolean) => {
    const newPanelDone = { ...panelDone, [frameId]: newState };
    setPanelDone(newPanelDone);
    
    const notificationFrameIds = NOTIFICATION_PANELS.map(p => p.frameId);
    setCompletedPanels(Object.keys(newPanelDone).filter(id => notificationFrameIds.includes(id) && newPanelDone[id]).length);

    if (estateId) {
      await saveFrameData(frameId, { completed: newState });
      await refresh();
      
      const steps = await loadPhaseProgress(PHASE);
      const row = steps.find((s) => s.stepId === STEP_ID);
      if (row?.status === 'not-started' || !row) {
        await updateStepProgress(STEP_ID, PHASE, { status: 'in-progress' });
      }
    }
  };

  const handleNoteChange = async (val: string) => {
    setNotes(val);
    if (estateId) {
      await saveFrameData(STEP_ID, { notes: val } as Record<string, unknown>);
    }
  };

  return (
    <div className={containerClassName}>
      <div className={contentColumnClassName}>
        {error && (
          <div className="mb-4 bg-orange-100 border border-orange-300 rounded-md p-3 text-orange-900 text-sm max-w-3xl mx-auto">
            {error}
          </div>
        )}

        <div className="flex items-center gap-3 mb-6">
          <button
            type="button"
            onClick={() => navigate(withEstatePath(OVERVIEW_ROUTE, estateId))}
            className="w-[35px] h-[35px] flex items-center justify-center text-grey-600 hover:text-blue-900 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className={titleClassName}>Notify people and organizations</h1>
        </div>

        <p className={subtitleClassName}>
          Let family, friends, and key organizations know about the passing in a simple and respectful way.
        </p>

        <div className="mb-8">
          <PlannerDataSection
            estateId={estateId ?? ''}
            plannerFrameId="frame-146"
            title="Contacts identified in estate plan"
            emptyMessage="No specific contacts were listed in the estate plan."
            renderData={renderPlanner146Summary}
          />
        </div>

        <ProgressHeader
          title="Initial notifications"
          variant={isPlanner ? 'planner' : 'default'}
          subtitle="Start with those closest, then work outward—keep it simple and take it one step at a time."
          completedSteps={completedPanels}
          totalSteps={NOTIFICATION_PANELS.length}
        />

        <div className="space-y-4 mt-8">
          {NOTIFICATION_PANELS.map((panel, index) => {
            const guidance = CHECKLIST_GUIDANCE[index];
            return (
              <ChecklistItem
                key={panel.frameId}
                id={panel.frameId}
                title={panel.title}
                description={panel.intro}
                whatThisIs={guidance.what}
                whyItMatters={guidance.why}
                bulletPoints={guidance.actions}
                checked={!!panelDone[panel.frameId]}
                onChange={handleToggleComplete}
              />
            );
          })}
        </div>

        {/* --- Notes Section --- */}
        <div className="mt-10 pt-10 border-t border-stone-200">
          <h3 className="text-lg font-semibold text-stone-900 mb-2">Notes</h3>
          <textarea
            className="w-full rounded-lg border border-stone-300 p-4 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-all min-h-[120px]"
            placeholder="Add details, locations, or reminders to keep track of your progress here"            
            value={notes}
            onChange={(e) => handleNoteChange(e.target.value)}
          />
        </div>

      </div>
    </div>
  );
}